const DEFAULT_COLOR = '#000000'
export const FIXED_OHLC_BAR_STROKE_WIDTH_PX = 2
export const FIXED_OHLC_BAR_TICK_WIDTH_PX = 5

function finiteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value)
}

function resolveBitmapWidth(scope) {
  if (finiteNumber(scope?.bitmapSize?.width)) return scope.bitmapSize.width
  if (finiteNumber(scope?.mediaSize?.width) && finiteNumber(scope?.horizontalPixelRatio)) {
    return scope.mediaSize.width * scope.horizontalPixelRatio
  }
  return scope?.context?.canvas?.width ?? 0
}

function fillHorizontal(ctx, x1, x2, y, thickness) {
  const left = Math.round(Math.min(x1, x2))
  const right = Math.round(Math.max(x1, x2))
  const top = Math.round(y - (thickness / 2))
  ctx.fillRect(left, top, Math.max(thickness, right - left), thickness)
}

function fillVertical(ctx, x, y1, y2, thickness) {
  const left = Math.round(x - (thickness / 2))
  const top = Math.round(Math.min(y1, y2))
  const bottom = Math.round(Math.max(y1, y2))
  ctx.fillRect(left, top, thickness, Math.max(thickness, bottom - top))
}

function resolveSlotWidth(points, index) {
  const prev = index > 0 ? points[index - 1].x : null
  const next = index < points.length - 1 ? points[index + 1].x : null
  const left = finiteNumber(prev) ? Math.abs(points[index].x - prev) : Infinity
  const right = finiteNumber(next) ? Math.abs(next - points[index].x) : Infinity
  const slot = Math.min(left, right)
  return Number.isFinite(slot) ? slot : Infinity
}

export class FixedOhlcBarPrimitive {
  constructor() {
    this._data = { bars: [], color: DEFAULT_COLOR }
    this._chart = null
    this._series = null
    this._requestUpdate = null
    this._paneViews = [new FixedOhlcBarPaneView(this)]
  }

  attached(param) {
    this._chart = param.chart
    this._series = param.series
    this._requestUpdate = param.requestUpdate
  }

  detached() {
    this._chart = null
    this._series = null
    this._requestUpdate = null
    this._data = { bars: [], color: DEFAULT_COLOR }
  }

  update(next) {
    this._data = {
      bars: Array.isArray(next?.bars) ? next.bars : [],
      color: next?.color || DEFAULT_COLOR,
    }
    if (typeof this._requestUpdate === 'function') this._requestUpdate()
  }

  updateAllViews() { /* renderer reads live state */ }

  paneViews() {
    return this._paneViews
  }
}

class FixedOhlcBarPaneView {
  constructor(primitive) {
    this._renderer = new FixedOhlcBarPaneRenderer(primitive)
  }

  zOrder() { return 'top' }

  renderer() { return this._renderer }
}

class FixedOhlcBarPaneRenderer {
  constructor(primitive) {
    this._primitive = primitive
  }

  draw(target) {
    const data = this._primitive._data
    const chart = this._primitive._chart
    const series = this._primitive._series
    if (!data?.bars?.length || !chart || !series) return

    target.useBitmapCoordinateSpace((scope) => {
      const ctx = scope.context
      const timeScale = chart.timeScale()
      const hpr = scope.horizontalPixelRatio || 1
      const vpr = scope.verticalPixelRatio || 1
      const strokeWidth = Math.max(1, Math.round(FIXED_OHLC_BAR_STROKE_WIDTH_PX * hpr))
      const preferredTickWidth = Math.max(
        strokeWidth,
        Math.round(FIXED_OHLC_BAR_TICK_WIDTH_PX * hpr),
      )
      const bitmapWidth = resolveBitmapWidth(scope)
      const xPad = preferredTickWidth + strokeWidth
      const points = []

      for (const bar of data.bars) {
        if (
          !finiteNumber(bar?.time) ||
          !finiteNumber(bar?.open) ||
          !finiteNumber(bar?.high) ||
          !finiteNumber(bar?.low) ||
          !finiteNumber(bar?.close)
        ) continue

        const xLogical = timeScale.timeToCoordinate(bar.time)
        if (xLogical == null) continue
        const x = xLogical * hpr
        if (finiteNumber(bitmapWidth) && bitmapWidth > 0 && (x < -xPad || x > bitmapWidth + xPad)) {
          continue
        }

        const highY = series.priceToCoordinate(bar.high)
        const lowY = series.priceToCoordinate(bar.low)
        const openY = series.priceToCoordinate(bar.open)
        const closeY = series.priceToCoordinate(bar.close)
        if (
          highY == null ||
          lowY == null ||
          openY == null ||
          closeY == null
        ) continue

        points.push({
          x,
          high: highY * vpr,
          low: lowY * vpr,
          open: openY * vpr,
          close: closeY * vpr,
        })
      }

      ctx.save()
      ctx.fillStyle = data.color
      for (let i = 0; i < points.length; i++) {
        const point = points[i]
        const slotWidth = resolveSlotWidth(points, i)
        const maxTickWidthForSlot = Math.floor((slotWidth - strokeWidth) / 2)
        if (maxTickWidthForSlot < strokeWidth) continue
        const tickWidth = Math.min(preferredTickWidth, maxTickWidthForSlot)

        fillVertical(ctx, point.x, point.high, point.low, strokeWidth)
        fillHorizontal(ctx, point.x - tickWidth, point.x, point.open, strokeWidth)
        fillHorizontal(ctx, point.x, point.x + tickWidth, point.close, strokeWidth)
      }
      ctx.restore()
    })
  }
}
